// =============================================================================
// projectVocab — return project-type-aware labels for workspace UI copy.
// =============================================================================
//
// Used to avoid hardcoded "dissertation" / "thesis" / "defense" wording when
// the user is writing a research article, systematic review, grant proposal,
// case report etc. The user wants the UI to feel ADAPTED to their project type.
//
// Usage:
//   import { vocab } from '@/lib/projectVocab';
//   const v = vocab(project?.project_type);
//   <h1>{v.manuscript}</h1>          // "dissertation" | "research article" | …
//   <p>Run {v.readinessLabel}</p>   // "defense audit" | "peer-review audit" | …
//
// Keep this file tiny — copy-only, no logic.

const TYPES = {
  dissertation: {
    manuscript: 'dissertation',
    Manuscript: 'Dissertation',
    section: 'chapter',
    Section: 'Chapter',
    readinessLabel: 'defense readiness audit',
    panel: 'Defense Panel',
    panelVerb: 'survive a defense',
    structureLabel: 'dissertation architecture',
  },
  book: {
    manuscript: 'monograph',
    Manuscript: 'Monograph',
    section: 'chapter',
    Section: 'Chapter',
    readinessLabel: 'publication readiness audit',
    panel: 'Reviewer Panel',
    panelVerb: 'pass editorial review',
    structureLabel: 'book architecture',
  },
  research_article: {
    manuscript: 'article',
    Manuscript: 'Article',
    section: 'section',
    Section: 'Section',
    readinessLabel: 'peer-review readiness audit',
    panel: 'Reviewer Panel',
    panelVerb: 'survive peer review',
    structureLabel: 'article structure',
  },
  systematic_review: {
    manuscript: 'systematic review',
    Manuscript: 'Systematic Review',
    section: 'section',
    Section: 'Section',
    readinessLabel: 'PRISMA readiness audit',
    panel: 'Reviewer Panel',
    panelVerb: 'meet PRISMA standards',
    structureLabel: 'review structure',
  },
  meta_analysis: {
    manuscript: 'meta-analysis',
    Manuscript: 'Meta-analysis',
    section: 'section',
    Section: 'Section',
    readinessLabel: 'peer-review readiness audit',
    panel: 'Reviewer Panel',
    panelVerb: 'pass methodological review',
    structureLabel: 'meta-analysis structure',
  },
  clinical_protocol: {
    manuscript: 'clinical protocol',
    Manuscript: 'Clinical Protocol',
    section: 'section',
    Section: 'Section',
    readinessLabel: 'protocol readiness audit',
    panel: 'Reviewer Panel',
    panelVerb: 'pass ethics review',
    structureLabel: 'protocol outline',
  },
  grant_proposal: {
    manuscript: 'grant proposal',
    Manuscript: 'Grant Proposal',
    section: 'section',
    Section: 'Section',
    readinessLabel: 'study-section readiness audit',
    panel: 'Reviewer Panel',
    panelVerb: 'pass study-section review',
    structureLabel: 'proposal outline',
  },
  case_report: {
    manuscript: 'case report',
    Manuscript: 'Case Report',
    section: 'section',
    Section: 'Section',
    readinessLabel: 'CARE readiness audit',
    panel: 'Reviewer Panel',
    panelVerb: 'meet CARE standards',
    structureLabel: 'case-report outline',
  },
  conference_abstract: {
    manuscript: 'abstract',
    Manuscript: 'Abstract',
    section: 'section',
    Section: 'Section',
    readinessLabel: 'abstract readiness audit',
    panel: 'Reviewer Panel',
    panelVerb: 'pass abstract review',
    structureLabel: 'abstract outline',
  },
  editorial: {
    manuscript: 'editorial',
    Manuscript: 'Editorial',
    section: 'section',
    Section: 'Section',
    readinessLabel: 'editorial readiness audit',
    panel: 'Reviewer Panel',
    panelVerb: 'pass editorial board',
    structureLabel: 'editorial outline',
  },
};

const DEFAULT = TYPES.research_article;

export function vocab(projectType) {
  if (!projectType) return TYPES.dissertation; // safer default for legacy projects
  return TYPES[projectType] || DEFAULT;
}

export function isSectionBased(projectType) {
  return ['dissertation', 'book'].includes(projectType);
}
